/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2023-2024 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2026 (c) Nico Brandt, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React, { useEffect, useState } from 'react';
import { CompanyDataActions } from './CompanyDataActions';
import { connect } from 'react-redux';
import { Dispatch } from '@reduxjs/toolkit';
import MsgListActions, { BootstrapColor_t } from 'MsgList/MsgListActions';
import { Company } from 'Api/generated';
import { AllowedRoles } from 'Contexts/Auth';

interface IRmCompanyProps {
  company: Company;
  companyList: Company[];
  onChange: () => void;
  onDeletedCompany: () => void;
}

const companyExistsInCompanyList = (companyList: Company[], editCompanyName: Company) => {
  return (
    companyList.find((c) => c.ShortName === editCompanyName.ShortName && c.Name === editCompanyName.Name) !== undefined
  );
};

type DispatchProps_t = ReturnType<typeof mapDispatchToProps>;

export function RmCompany({
  company,
  companyList,
  AddMsg,
  UpdateCompany,
  DeleteCompany,
  onChange,
  onDeletedCompany,
}: IRmCompanyProps & DispatchProps_t) {
  async function rmCompany() {
    let confirmation_request = 'Are sure you want to delete this company with all clients?';
    if (window.confirm(confirmation_request) == true) {
      console.log('Remove the Company' + company.Name);
      confirmation_request = 'Deleted!';
      DeleteCompany(company);
      onDeletedCompany();
      AddMsg('Deleted Company in DB!', 'danger');
    } else {
      confirmation_request = 'Canceled';
    }
  }

  const [edit, setEdit] = useState<boolean>(false);
  const [nameAlreadyExist, setnameAlreadyExist] = useState(false);
  const [editCompanyName, seteditCompanyName]: [Company, any] = useState({
    Id: '',
    Name: '',
    ShortName: '',
  });

  useEffect(() => {
    seteditCompanyName({ Name: company.Name, ShortName: company.ShortName });
  }, [edit, company.Name, company.ShortName]);

  useEffect(() => {
    const nameAlreadyExist = companyExistsInCompanyList(companyList, editCompanyName);
    setnameAlreadyExist(nameAlreadyExist);
  }, [editCompanyName, companyList]);

  const SaveEditedData = async () => {
    UpdateCompany(editCompanyName);
    AddMsg('Saved changes in DB!', 'success ');
    setEdit(!edit);
    onChange();
  };

  const handleShortName = (value: string) => {
    seteditCompanyName({ ...editCompanyName, ShortName: value });
  };

  const handleName = (value: string) => {
    seteditCompanyName({ ...editCompanyName, Name: value });
  };

  return (
    <div className="row">
      <div className="form-group col-md-2">
        <label>Company name</label>
        {edit && (
          <input
            className="form-control"
            name="Name"
            defaultValue={company.Name}
            onChange={(e) => handleName(e.target.value)}
            type="text"
          />
        )}
        {!edit && <div>{company.Name}</div>}
      </div>
      <div className="form-group col-md-2">
        <label>Short Name</label>
        {edit && (
          <input
            className="form-control"
            name="ShortName"
            defaultValue={company.ShortName}
            onChange={(e) => handleShortName(e.target.value)}
            type="text"
          />
        )}
        {!edit && <div>{company.ShortName}</div>}
      </div>
      <AllowedRoles roles={['SuperAdmin']}>
        <div className="form-group col-md-3 align-self-end">
          <label> </label>

          <button className={!edit ? 'btn btn-primary ' : 'btn btn-secondary '} onClick={() => setEdit(!edit)}>
            {!edit ? 'Edit Companyname' : 'Cancel'}
          </button>

          <label> </label>
          {edit && (
            <button
              className={
                nameAlreadyExist && company.Name != editCompanyName.Name ? 'btn btn-danger' : 'btn btn-primary'
              }
              onClick={() => SaveEditedData()}
              disabled={
                (nameAlreadyExist && company.Name != editCompanyName.Name) ||
                editCompanyName.Name === '' ||
                editCompanyName.ShortName === ''
              }
            >
              {nameAlreadyExist && company.Name != editCompanyName.Name ? 'Name already exist' : 'Save changes'}
            </button>
          )}
        </div>
      </AllowedRoles>
      <div className="form-group col-md-2 align-self-end">
        <label> </label>
        <AllowedRoles roles={['SuperAdmin']}>
          <button className="btn btn-secondary " onClick={() => rmCompany()}>
            Remove Company
          </button>
        </AllowedRoles>
      </div>
    </div>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    AddMsg: (msg: string, severity: BootstrapColor_t) => MsgListActions.Add(msg, severity)(dispatch),
    UpdateCompany: (editCompany: Company) => CompanyDataActions.UpdateCompany(editCompany)(dispatch),
    DeleteCompany: (deleteCompany: Company) => CompanyDataActions.DeleteCompany(deleteCompany)(dispatch),
  };
};

export default connect(undefined, mapDispatchToProps)(RmCompany);
